import { TitleCasePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Injector,
  OnInit,
  Output,
  untracked,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BasketService } from '@basket';
import { StickyDirective } from '@common';
import {
  GridColumnComponent,
  GridRowComponent,
  LinkComponent,
} from '@components';
import { CONFIG } from '@config';
import { CustomerService } from '@customer';
import { BasketComponent } from '@data-access';
import {
  AddressToArrayPipe,
  CheckboxComponent,
  EmailInputComponent,
  FormControlValidators,
  FormDisabledDirective,
  FormFieldComponent,
  FormFieldDirective,
  FormSubmitButtonComponent,
  FormSubmitDirective,
  highlightInvalidControls,
  TelephoneInputComponent,
} from '@forms';
import {
  ConjunctionPipe,
  provideTranslationDictionaryMapping,
  TranslatePipe,
  TranslateService,
} from '@i18n';
import { QMSanitiseDirective } from '@qm';
import {
  emailFormField,
  phoneFormField,
} from '../../constants/checkout-form-fields.constant';
import { FeatureCheckoutTranslationDictionary } from '../../i18n/feature-checkout-dictionary.interface';
import { translation } from '../../i18n/translation.constant';
import { ContactDetailsFormField } from '../../models/contact-details/contact-details-form-field.enum';
import { ContactDetailsFormValue } from '../../models/contact-details/contact-details-form-value.type';
import { ContactDetailsForm } from '../../models/contact-details/contact-details-form.interface';
import { ContactDetailsFormBuilder } from '../../services/contact-details-form-builder/contact-details-form-builder.service';
import { UpsellCheckboxComponent } from '../upsell-checkbox/upsell-checkbox.component';

@Component({
  selector: 'app-contact-details-form',
  templateUrl: './contact-details-form.component.html',
  styleUrl: './contact-details-form.component.scss',
  standalone: true,
  providers: [
    ContactDetailsFormBuilder,
    provideTranslationDictionaryMapping(translation),
  ],
  imports: [
    AddressToArrayPipe,
    BasketComponent,
    CheckboxComponent,
    ConjunctionPipe,
    EmailInputComponent,
    FormDisabledDirective,
    FormFieldComponent,
    FormFieldDirective,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    GridColumnComponent,
    GridRowComponent,
    LinkComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    StickyDirective,
    TelephoneInputComponent,
    TitleCasePipe,
    TranslatePipe,
    UpsellCheckboxComponent,
  ],
})
export class ContactDetailsFormComponent implements OnInit {
  private readonly config = inject(CONFIG);
  private readonly basketService = inject(BasketService);
  private readonly customerService = inject(CustomerService);
  private readonly formBuilderService = inject(ContactDetailsFormBuilder);
  private readonly translateService = inject(
    TranslateService<FeatureCheckoutTranslationDictionary>,
  );
  private readonly injector = inject(Injector);
  readonly clientName = this.config.clientName;
  readonly customerDetails = this.customerService.customerDetails;
  readonly domainId = this.config.domainId;
  readonly privacyPolicyLink = this.config.privacyPolicyLink;
  readonly privacyPolicyLinkExternal = this.config.privacyPolicyLinkExternal;
  readonly customerHasSelectedToPurchasePlan =
    this.customerService.customerSelectedToPurchasePlan;
  readonly customerHasSelectedToRegisterOnly =
    this.customerService.customerSelectedToRegisterOnly;
  readonly showMultiPlanJourney =
    this.basketService._showMultiPlanJourney.asReadonly();
  readonly hasUpsellQuote = computed(
    () => !!this.basketService.basketItemQuoteDetailsPaymentOption(),
  );

  @Output() submitted = new EventEmitter<ContactDetailsFormValue>();
  form?: FormGroup<ContactDetailsForm>;
  formSubmitted = false;

  readonly formFields = {
    email: emailFormField,
    phone: phoneFormField,
  };

  ngOnInit(): void {
    this.form = this.formBuilderService.build();

    const emailControl = this.form?.get(ContactDetailsFormField.Email);
    const requiredValidator = FormControlValidators.required(
      this.translateService.translate(
        'CONTACT_DETAILS_FORM_CONDITIONAL_EMAIL_REQUIRED',
      ),
    );

    effect(
      () => {
        const hasPlan = this.customerHasSelectedToPurchasePlan();

        untracked(() => {
          if (hasPlan) {
            emailControl?.addValidators(requiredValidator);
          } else {
            emailControl?.removeValidators(requiredValidator);
          }
          emailControl?.updateValueAndValidity({ emitEvent: false });
        });
      },
      { injector: this.injector },
    );
  }

  submitForm(formValue: ContactDetailsFormValue): void {
    this.formSubmitted = true;
    this.submitted.emit(formValue);
  }

  submit(): void {
    if (!this.form || this.form.disabled) return;

    if (this.form.invalid) {
      highlightInvalidControls(this.form);
      return;
    }

    this.formSubmitted = true;
    this.submitted.emit(
      this.form.getRawValue() as ContactDetailsFormValue,
    );
  }
}
